import { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Paper,
  CircularProgress, Alert, Link,
} from '@mui/material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import { getHostBoardSlug } from '../jobBoard/hostSlug';

const PortalOtpScreen = ({ access, positionId, onSuccess, onBack }) => {
  const { t } = useTranslation();
  // access: { dni, country, email, maskedEmail } — email null si el documento ya estaba registrado
  const displayEmail = access?.email || access?.maskedEmail || '';
  // Slug del subdominio ({slug}.amandahr.ai): marca el origen del candidato
  // nuevo al registrarse desde la web de una organización (source).
  const hostSlug = getHostBoardSlug();
  const otpPayloadBase = {
    dni: access?.dni,
    country: access?.country,
    ...(access?.email ? { email: access.email } : {}),
    ...(hostSlug ? { slug: hostSlug } : {}),
    // Con vacante el código puede ir al email de la ficha del proyecto
    // (PLAN_MASIVO_PORTAL, fase 0): verificar y reenviar usan el mismo destino
    ...(positionId ? { positionId } : {}),
    // Nacionalidad declarada y pasaporte (el acceso pide el documento según la
    // nacionalidad, PLAN_MASIVO_PORTAL): la nacionalidad queda en la cuenta
    ...(access?.nationality ? { nationality: access.nationality } : {}),
    ...(access?.documentType === 'passport' ? { documentType: 'passport' } : {}),
  };
  // El código fue al email ya registrado (cuenta o ficha), no a uno escrito ahora
  const sentToRegistered = Boolean(access?.maskedEmail);
  // Acceso con correo (PLAN_MASIVO_PORTAL): el portal entra siempre así; el
  // acceso por documento queda para entrar a la cuenta dueña de un documento
  const emailMode = access?.mode === 'email';
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  // Cooldown de reenvío (el backend aplica el mismo límite; esto es UX)
  const [cooldown, setCooldown] = useState(60);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // solo un dígito
    setOtp(newOtp);
    setError(null);

    // Avanzar al siguiente input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setError(t('portal.otpIncomplete'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.post(
        emailMode ? `/public/candidates/email/verify-otp` : `/public/candidates/verify-otp`,
        emailMode ? { email: access.email, otp: code } : { ...otpPayloadBase, otp: code }
      );

      if (response.data.response?.status_response === 'OK') {
        const {
          sessionToken, candidateId, needsRegistration, registrationToken, needsIdentity,
        } = response.data.response;

        // Correo sin cuenta: se crea en "Completa tus datos" (aún no hay cuenta)
        if (needsRegistration) {
          onSuccess(null, null, null, { registrationToken });
          return;
        }

        // Obtener datos del candidato
        let candData = null;
        try {
          const meResponse = await api.get(
            `/public/candidates/me`,
            { headers: { 'x-candidate-token': sessionToken } }
          );
          if (meResponse.data.response?.status_response === 'OK') {
            candData = meResponse.data.response.data;
          }
        } catch (err) {
          console.error(err);
        }

        onSuccess(sessionToken, candidateId, candData, { needsIdentity: Boolean(needsIdentity) });
      } else {
        setError(t('portal.otpInvalid'));
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 429) {
        setError(t('portal.tooManyAttempts'));
      } else if (err.response?.status === 409) {
        setError(t('portal.emailInUse'));
      } else {
        setError(t('portal.otpInvalid'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendSuccess(false);
    setError(null);
    try {
      if (emailMode) {
        await api.post(`/public/candidates/email/request-otp`, { email: access.email });
      } else {
        await api.post(
          `/public/candidates/request-otp`,
          otpPayloadBase
        );
      }
      setResendSuccess(true);
      setOtp(['', '', '', '', '', '']);
      setCooldown(60);
      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 429) {
        setError(t('portal.tooManyRequests'));
        setCooldown(60);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', px: 2, pt: { xs: '6vh', md: '10vh' } }}>
      <Box sx={{ width: '100%', maxWidth: 480 }}>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/amandalogoh.png"
            alt="Amanda HR"
            sx={{ height: 40, mb: 3 }}
          />
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            {t('portal.otpTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: sentToRegistered && positionId ? 1 : 3 }}>
            {t(sentToRegistered ? 'portal.otpSubtitleRegistered' : 'portal.otpSubtitle', { email: displayEmail })}
          </Typography>
          {sentToRegistered && positionId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              {t('portal.noAccessToEmail')}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>{t('portal.otpResendSuccess')}</Alert>
          )}

          {/* Cajas OTP */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3 }} onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <TextField
                key={i}
                value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                inputRef={(el) => (inputRefs.current[i] = el)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    padding: '12px 0',
                    width: 48,
                  },
                }}
                sx={{ width: 56 }}
              />
            ))}
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleVerify}
            disabled={loading || otp.join('').length < 6}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5, mb: 2 }}
          >
            {loading ? t('portal.verifying') : t('portal.verify')}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={onBack}
              sx={{ color: 'text.secondary' }}
            >
              {t('portal.back')}
            </Link>
            <Link
              component="button"
              variant="body2"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              sx={{ color: cooldown > 0 ? 'text.disabled' : '#6A5AF9' }}
            >
              {cooldown > 0
                ? t('portal.resendIn', { seconds: cooldown })
                : resending
                  ? t('portal.resending')
                  : t('portal.resend')}
            </Link>
          </Box>
        </Paper>

      </Box>
    </Box>
  );
};

export default PortalOtpScreen;